import { useState } from 'react';
import { Upload, Play, CheckCircle, AlertCircle, Loader2, FileText } from 'lucide-react';
import api from '../services/api';

interface ImportResult {
  status: string;
  total?: number;
  imported?: number;
  updated?: number;
  skipped?: number;
  errors?: number;
  message?: string;
  contacts_updated?: number;
}

interface StepStatus {
  name: string;
  endpoint: string;
  label: string;
  description: string;
  status: 'pending' | 'running' | 'completed' | 'error';
  result?: ImportResult;
}

const IMPORT_STEPS: Omit<StepStatus, 'status'>[] = [
  { name: 'companies', endpoint: '/linkedin/import/companies', label: 'Companies', description: 'Import Company Follows (201 companies)' },
  { name: 'connections', endpoint: '/linkedin/import/connections', label: 'Connections', description: 'Import LinkedIn connections (1,339 contacts)' },
  { name: 'contacts', endpoint: '/linkedin/import/contacts', label: 'Imported Contacts', description: 'Import phone/email contacts with dedup (9,298 contacts)' },
  { name: 'endorsements', endpoint: '/linkedin/import/endorsements', label: 'Endorsements', description: 'Import skill endorsements (2,500+ endorsements)' },
  { name: 'recommendations', endpoint: '/linkedin/import/recommendations', label: 'Recommendations', description: 'Import LinkedIn recommendations (9 testimonials)' },
  { name: 'messages', endpoint: '/linkedin/import/messages', label: 'Messages', description: 'Import message history (16,768 messages)' },
];

export default function LinkedInImportPage() {
  const [steps, setSteps] = useState<StepStatus[]>(
    IMPORT_STEPS.map(s => ({ ...s, status: 'pending' as const }))
  );
  const [isRunning, setIsRunning] = useState(false);
  const [warmthStatus, setWarmthStatus] = useState<'pending' | 'running' | 'completed' | 'error'>('pending');
  const [warmthResult, setWarmthResult] = useState<ImportResult | null>(null);
  const [fullImportRunning, setFullImportRunning] = useState(false);

  const runStep = async (index: number) => {
    const step = steps[index];
    setSteps(prev => prev.map((s, i) => i === index ? { ...s, status: 'running' } : s));

    try {
      const res = await api.post(step.endpoint);
      setSteps(prev => prev.map((s, i) =>
        i === index ? { ...s, status: 'completed', result: res.data } : s
      ));
    } catch (err: any) {
      setSteps(prev => prev.map((s, i) =>
        i === index ? { ...s, status: 'error', result: { status: 'error', message: err.message } } : s
      ));
    }
  };

  const runAllSteps = async () => {
    setIsRunning(true);
    for (let i = 0; i < steps.length; i++) {
      await runStep(i);
    }
    // Calculate warmth after all imports
    setWarmthStatus('running');
    try {
      const res = await api.post('/linkedin/warmth/recalculate');
      setWarmthResult(res.data);
      setWarmthStatus('completed');
    } catch {
      setWarmthStatus('error');
    }
    setIsRunning(false);
  };

  const runFullImport = async () => {
    setFullImportRunning(true);
    try {
      const res = await api.post('/linkedin/import/full');
      const results = res.data.results;
      // Map results back to steps
      const stepNames = ['companies', 'connections', 'imported_contacts', 'endorsements', 'recommendations', 'messages'];
      setSteps(prev => prev.map((s, i) => ({
        ...s,
        status: 'completed' as const,
        result: results[stepNames[i]] || { status: 'completed' }
      })));
      if (results.warmth) {
        setWarmthResult(results.warmth);
        setWarmthStatus('completed');
      }
    } catch (err: any) {
      setSteps(prev => prev.map(s => ({ ...s, status: 'error' })));
    }
    setFullImportRunning(false);
  };

  const StatusIcon = ({ status }: { status: string }) => {
    switch (status) {
      case 'running': return <Loader2 size={18} className="animate-spin" style={{ color: 'var(--netrun-green)' }} />;
      case 'completed': return <CheckCircle size={18} style={{ color: '#22c55e' }} />;
      case 'error': return <AlertCircle size={18} style={{ color: '#ef4444' }} />;
      default: return <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid var(--border-primary)' }} />;
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-display mb-2" style={{ color: 'var(--text-primary)' }}>
          LinkedIn Data Import
        </h1>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Import your LinkedIn export data into KOG CRM. Connections become contacts,
          companies become organizations, and messages are preserved with full conversation history.
        </p>
      </div>

      {/* Source Info */}
      <div
        className="rounded-lg p-4 mb-6"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <FileText size={18} style={{ color: 'var(--netrun-green)' }} />
          <h3 className="font-medium" style={{ color: 'var(--text-primary)' }}>Data Source</h3>
        </div>
        <p className="text-xs font-mono mb-1" style={{ color: 'var(--text-muted)' }}>
          LinkedIn Export (March 20, 2026)
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          {[
            { label: 'Connections', count: '1,339' },
            { label: 'Contacts', count: '9,298' },
            { label: 'Messages', count: '16,768' },
            { label: 'Companies', count: '201' },
          ].map(item => (
            <div key={item.label} className="text-center py-2 rounded" style={{ background: 'var(--bg-primary)' }}>
              <div className="text-lg font-bold" style={{ color: 'var(--netrun-green)' }}>{item.count}</div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{item.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={runAllSteps}
          disabled={isRunning || fullImportRunning}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all"
          style={{
            background: isRunning ? 'var(--bg-tertiary)' : 'var(--netrun-green)',
            color: isRunning ? 'var(--text-muted)' : '#000',
            cursor: isRunning ? 'not-allowed' : 'pointer',
          }}
        >
          {isRunning ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
          Import Step by Step
        </button>
        <button
          onClick={runFullImport}
          disabled={isRunning || fullImportRunning}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all"
          style={{
            background: fullImportRunning ? 'var(--bg-tertiary)' : 'transparent',
            color: fullImportRunning ? 'var(--text-muted)' : 'var(--netrun-green)',
            border: '1px solid var(--netrun-green)',
            cursor: fullImportRunning ? 'not-allowed' : 'pointer',
          }}
        >
          {fullImportRunning ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          Full Import (All at Once)
        </button>
      </div>

      {/* Step Progress */}
      <div className="space-y-2 mb-6">
        {steps.map((step, i) => (
          <div
            key={step.name}
            className="flex items-center justify-between rounded-lg px-4 py-3"
            style={{
              background: step.status === 'running' ? 'rgba(144, 185, 171, 0.08)' : 'var(--bg-secondary)',
              border: `1px solid ${step.status === 'running' ? 'var(--netrun-green)' : 'var(--border-primary)'}`,
            }}
          >
            <div className="flex items-center gap-3">
              <StatusIcon status={step.status} />
              <div>
                <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  {step.label}
                </div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {step.description}
                </div>
              </div>
            </div>
            <div className="text-right">
              {step.result && step.status === 'completed' && (
                <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  {step.result.imported !== undefined && (
                    <span style={{ color: '#22c55e' }}>{step.result.imported} imported</span>
                  )}
                  {step.result.updated !== undefined && step.result.updated > 0 && (
                    <span className="ml-2" style={{ color: 'var(--netrun-green)' }}>{step.result.updated} updated</span>
                  )}
                  {step.result.skipped !== undefined && step.result.skipped > 0 && (
                    <span className="ml-2" style={{ color: 'var(--text-muted)' }}>{step.result.skipped} skipped</span>
                  )}
                </div>
              )}
              {step.status === 'error' && step.result?.message && (
                <div className="text-xs" style={{ color: '#ef4444' }}>{step.result.message}</div>
              )}
              {step.status === 'pending' && !isRunning && !fullImportRunning && (
                <button
                  onClick={() => runStep(i)}
                  className="text-xs px-3 py-1 rounded"
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border-primary)',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                  }}
                >
                  Run
                </button>
              )}
            </div>
          </div>
        ))}

        {/* Warmth calculation step */}
        <div
          className="flex items-center justify-between rounded-lg px-4 py-3"
          style={{
            background: warmthStatus === 'running' ? 'rgba(144, 185, 171, 0.08)' : 'var(--bg-secondary)',
            border: `1px solid ${warmthStatus === 'running' ? 'var(--netrun-green)' : 'var(--border-primary)'}`,
          }}
        >
          <div className="flex items-center gap-3">
            <StatusIcon status={warmthStatus} />
            <div>
              <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                Warmth Scores
              </div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Calculate relationship warmth for all contacts
              </div>
            </div>
          </div>
          <div className="text-right">
            {warmthResult && warmthStatus === 'completed' && (
              <div className="text-xs" style={{ color: '#22c55e' }}>
                {warmthResult.contacts_updated} contacts scored
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Import Notes */}
      <div
        className="rounded-lg p-4 text-xs"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', color: 'var(--text-muted)' }}
      >
        <h4 className="font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Import Notes</h4>
        <ul className="space-y-1 list-disc list-inside">
          <li>Connections are deduplicated by email, LinkedIn URL, or name + company</li>
          <li>Imported Contacts (phone/email) are matched against existing connections before creating new records</li>
          <li>Messages are linked to contacts via LinkedIn profile URL matching</li>
          <li>Warmth scores are calculated based on connection age, messages, endorsements, and recommendations</li>
          <li>Re-running import steps will skip already-imported records</li>
        </ul>
      </div>
    </div>
  );
}
